import { Trans, useLingui } from "@lingui/react/macro";
import { XIcon } from "lucide-react";

import { VoiceWaveform } from "./VoiceWaveform";

export type ComposerVoiceBarMode = "recording" | "transcribing" | "recorded";

export function formatVoiceClipDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function ComposerVoiceRecordingBar(props: {
  mode: ComposerVoiceBarMode;
  stream: MediaStream | null;
  audio: Blob | null;
  durationMs: number | null;
  theme: "light" | "dark";
  onCancel: () => void;
}) {
  const { t } = useLingui();
  const waveformSource =
    props.mode === "recording" ? props.stream : props.mode === "recorded" ? props.audio : null;
  const cancelLabel =
    props.mode === "recording"
      ? t`Cancel recording`
      : props.mode === "transcribing"
        ? t`Cancel transcription`
        : t`Delete recording`;
  return (
    <div
      data-chat-composer-voice-bar={props.mode}
      className="flex min-w-0 flex-1 items-center gap-3 pr-3"
    >
      <button
        type="button"
        className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted text-foreground transition-all duration-150 hover:bg-muted/80 hover:scale-105 sm:h-8 sm:w-8"
        onPointerDown={(event) => event.preventDefault()}
        onClick={props.onCancel}
        aria-label={cancelLabel}
      >
        <XIcon className="size-4" />
      </button>
      {props.mode === "transcribing" ? (
        <div className="min-w-0 flex-1 animate-pulse truncate text-center text-sm text-muted-foreground motion-reduce:animate-none">
          <Trans>Transcribing</Trans>
        </div>
      ) : waveformSource ? (
        <VoiceWaveform source={waveformSource} theme={props.theme} />
      ) : (
        <div className="min-w-0 flex-1" />
      )}
      {props.mode === "recorded" && props.durationMs !== null ? (
        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {formatVoiceClipDuration(props.durationMs)}
        </span>
      ) : null}
    </div>
  );
}
